import type { Currency } from '@ps/web-core-enums';

import type { CasinoIcon, MosaicLayout } from '@swf/casino';

export enum Feature {
  Jackpot = 1,
  EvoVIP = 2,
  EvoFT = 4,
  FreeSpin = 8,
  BonusRnd = 16,
  MGA = 32,
  IGT = 64
}

export enum MarketingTag {
  Race = 'Race',
  Exclusive = 'Exclusive',
  New = 'New',
  Multiplier100 = 'multiplier100',
  Multiplier300 = 'multiplier300',
  Multiplier500 = 'multiplier500',
  Multiplier1000 = 'multiplier1000',
  Multiplier2000 = 'multiplier2000',
  Multiplier2500 = 'multiplier2500',
  Multiplier5000 = 'multiplier5000',
  Multiplier7500 = 'multiplier7500',
  Multiplier10000 = 'multiplier10000',
  Multiplier15000 = 'multiplier15000',
  Multiplier20000 = 'multiplier20000',
  Hot = 'Hot',
  HighRoller = 'High Roller',
  LiveLabel = 'Live Label',
  Slots = 'Slots',
  GameInfo = 'GameInfo'
}

export enum MarketingOverlayTag {
  Exclusive = 'o_Exclusive'
}

export enum OverlayHierarchy {
  Jackpot = 'jackpot_exclusive',
  Exclusive = 'exclusive_jackpot'
}

export enum PlayType {
  Single = 1,
  Multi = 2
}

export enum JoinReason {
  Play = 0,
  Observe = 1,
  Resume = 3
}

export interface GameLike {
  type?: string;
  images: CasinoGameImage;
}

export const promoTypes = ['promo-game', 'promo-modal', 'promo-frame', 'promo-tab'];
export type PromoType = (typeof promoTypes)[number];

export interface Promo extends Required<GameLike> {
  type: PromoType;
  url?: string;
  path?: string;
  index: number;
  title?: string;
  game?: { gametype: number; variant: number };
}

export interface CasinoGame extends GameLike {
  name: string;
  gametype: number;
  variant: number;
  gameMinBet: number;
  gameMaxBet: number;
  jackpot?: Jackpot;
  isFavorite?: boolean;
  tags?: string[];
  tables: CasinoTable[];
  feature?: Feature;
  playCount?: number;
  desc?: string;
  fsb?: boolean;
  featureNames?: string[];
}

export interface LiveStatus {
  numPlayers: number;
  open?: boolean;
  openingDateTime?: number;
  multiplier?: number;
  show?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface LiveCasinoGame extends CasinoGame {
  currency: Currency;
  minBet: number;
  maxBet: number;
  tId: number;
  ttId: number;
  maxSeats?: number;
  statusMeta: LiveStatus;
}

export interface CasinoTable {
  currency: Currency;
  features: number;
  fsbId?: number;
  id: number;
  maxBet: number;
  maxSeats: number;
  numPlayers?: number;
  minBet: number;
  playType: PlayType;
  playMoney?: number;
  featureNames?: string[];
  properties?: Properties;
}

export interface CasinoGameInstance {
  cur: string;
  fsbId: number;
  ftrs: number;
  gSet: {
    lUrl: string;
    initp: string;
  };
  gType: number;
  lm: number;
  maxBet: number;
  maxS: number;
  mbin: number;
  minBet: number;
  numOf: number;
  pFF: number;
  pType: number;
  sAddr: string;
  sInst: string;
  sNum: number;
  tId: number;
  tNm: string;
  tech: number;
  ttId: number;
  ufgId: number;
  uid: string;
  url: string;
  vId: number;
  vName: string;
  vType: number;
  xtk?: string;
  launchKey?: string;
  lines?: string;
  holdRate?: number;
  featureNames?: string;
  tags?: string;
}

export interface CasinoInterruptedGame {
  created: number;
  currency: Currency;
  features: number;
  fsbId: number;
  gametype: number;
  id: number;
  launchMode: number;
  lsource: number;
  maxBet: number;
  minBet: number;
  name: string;
  oBet: number;
  platforms: number;
  playCount: number;
  playMoney: number;
  tId: number;
  tech: number;
  ufgId: number;
  uid: string;
  vId: number;
  variant: number;
  xtk: string;
}

export type CasinoGameImage = { [I in CasinoIcon]?: string };

export interface PlayerFSB {
  userRollId: number;
  ttId: number;
  betPerSpin: number;
  totalSpins: number;
  remainingSpins: number;
  currency: Currency;
}

export interface Jackpot {
  currency: Currency;
  value: number;
  key: number;
}

export interface StreamData<T extends GameLike> {
  games: T[];
  subscriptions: { [path: string]: string };
}

export interface TrackedStreamData<T extends GameLike> extends StreamData<T> {
  loading: boolean;
}

export interface PromoStream {
  version: number;
  data: Promo[][];
  i18n?: { [id: string]: Promo[][] };
}

export interface Page {
  complete: boolean;
  games: CasinoGame[];
  page: number;
}

export enum MosaicContext {
  None,
  BigIcon,
  SmallIcon
}

export interface MosaicColumnConfig {
  gamesPer: number;
  serviceName: string;
  stream?: (gamesPer: number) => GameLike[];
}

export interface MosaicLocalConfig {
  layout: MosaicLayout;
  columnConfigs: MosaicColumnConfig[];
  pages?: number;
}

export interface Properties {
  lines: string;
  holdRate: number;
  multiplier?: boolean;
  vendorName?: string;
  wilds?: boolean;
}

export enum IconTypes {
  Poster = 'icnPoster',
  Square = 'icnSquare',
  Wide = 'icnWide'
}

export type TileType = 'poster' | 'wide';
